/**
 * @desc: 保存筛选器方案弹窗（含筛选项勾选 + 值预览）
 * 预览渲染委托给 controls/<type>/ 中的控件定义，
 * SaveFilterPopup 渲染时按需组装 preview。
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useIntl } from 'umi';
import { Modal, Button, Form, Input, Checkbox, CheckboxGroup } from 'tea-component';
import useMountedRef from '@intelligence/v3/_hooks/useMountedRef';
import { useFilterOptionList } from '@intelligence/hooks/useFilterOptionList';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { filterTypeRegistry } from '@intelligence/v3/filterCore/registry';
import { getPreviewTitle, getSafePreviewFallbackText } from '../../utils/previewHelpers';

import type { IFilterItemConfig, IFilterValues } from '@intelligence/v3/filterCore/types';

import '@intelligence/assets/scss/TopCharts/components/savePopup.scss';

interface ISaveFilterPopupProps extends IBaseProps {
  /** 弹窗是否可见 */
  visible: boolean;
  /** 筛选器配置列表 */
  items: IFilterItemConfig[];
  /** 当前已有的方案名称 */
  existingNames?: string[];
  /** 当前筛选器的 draft 值（要保存的内容） */
  currentValue: IFilterValues;
  /** 当前是否达到最大存储数量 */
  isAtSaveLimit?: boolean;
  /** 保存接口 */
  updateFilterSave: (params: { name: string; data: string }) => Promise<boolean>;
  /** 保存成功回调 */
  onSaved?: (name: string, values: IFilterValues) => void;
  /** 关闭弹窗回调 */
  onClose: () => void;
}

const MAX_NAME_LENGTH = 30;

/**
 * 通用回退预览
 */
const RenderFallbackPreview: React.FC<{
  item: IFilterItemConfig;
  valueItem: any;
}> = (props) => {
  const { item, valueItem } = props;
  const { formatMessage } = useIntl();
  const text = getSafePreviewFallbackText(valueItem);

  return (
    <>
      <span className="model-form-text">{getPreviewTitle(item, formatMessage)}</span>
      <span className="model-form-context">{text}</span>
    </>
  );
};

/**
 * 根据筛选器类型渲染值预览
 * 优先从控件模块获取 renderPreview，否则使用通用回退
 */
const renderItemPreview = (
  item: IFilterItemConfig,
  value: any,
  previewCtx: {
    dataSourceOptions?: any[];
    platformOptions?: any[];
  },
): React.ReactNode => {
  const entry = filterTypeRegistry.getResolved(item.type, previewCtx);
  if (entry?.renderPreview) {
    return entry.renderPreview(item, value);
  }

  return <RenderFallbackPreview item={item} valueItem={value} />;
};

const SaveFilterPopup: React.FC<ISaveFilterPopupProps> = (props) => {
  const {
    visible,
    existingNames = [],
    currentValue,
    items,
    isAtSaveLimit = false,
    updateFilterSave,
    onSaved,
    onClose,
  } = props;

  const { formatMessage } = useIntl();

  const isMountedRef = useMountedRef();

  const { dataSourceFilterOptionsAll, googlePlatformFilterOptions } = useFilterOptionList({ formatMessage });

  const previewCtx = useMemo(() => {
    return {
      dataSourceOptions: dataSourceFilterOptionsAll,
      platformOptions: googlePlatformFilterOptions,
    };
  }, [dataSourceFilterOptionsAll, googlePlatformFilterOptions]);

  const [filterName, setFilterName] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const hasInitializedVisibleRef = useRef(false);

  const visibleItems = useMemo(() => items.filter((item) => item.visible !== false), [items]);
  const defaultChecks = useMemo(() => visibleItems.map((item) => item.field), [visibleItems]);
  const [checkValues, setCheckValues] = useState<string[]>(defaultChecks);
  const trimmedFilterName = filterName.trim();
  const isReplacingExisting = isNotEmpty(trimmedFilterName) && existingNames.includes(trimmedFilterName);
  const isCreateBlockedByLimit = isAtSaveLimit && !isReplacingExisting;

  // 仅在弹窗打开时初始化默认勾选，避免用户勾选中途被 items 变化打断
  useEffect(() => {
    if (visible && !hasInitializedVisibleRef.current) {
      hasInitializedVisibleRef.current = true;
      setCheckValues(defaultChecks);
    }
  }, [defaultChecks, visible]);

  /** 关闭弹窗时恢复输入、校验和勾选状态，下一次打开重新初始化 */
  const handleClose = (): void => {
    setFilterName('');
    setValidationMessage('');
    hasInitializedVisibleRef.current = false;
    setCheckValues(defaultChecks);
    onClose();
  };

  /** 校验方案名称，同时区分覆盖已有方案和新建数量上限 */
  const handleNameChange = (name: string): void => {
    setFilterName(name);
    const trimmed = name.trim();

    if (isEmpty(trimmed)) {
      setValidationMessage('');
      return;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      setValidationMessage(formatMessage({ id: 'dbi_filterSave_input_maximum' }, { max: MAX_NAME_LENGTH }));
      return;
    }
    if (existingNames.includes(trimmed)) {
      setValidationMessage(formatMessage({ id: 'dbi_filterSave_replace_setting' }));
      return;
    }
    if (isAtSaveLimit) {
      setValidationMessage(formatMessage({ id: 'dbi_filterSave_my_filter_top10' }));
      return;
    }
    setValidationMessage('');
  };

  /**
   * 保存当前勾选字段。
   * 只保存 draft 中被用户勾选的字段，不改变 Filter 当前值。
   */
  const handleConfirm = async (): Promise<void> => {
    if (isEmpty(trimmedFilterName) || isCreateBlockedByLimit) {
      return;
    }

    const filteredValue: Record<string, any> = {};
    checkValues.forEach((field) => {
      if (currentValue[field] !== undefined) {
        filteredValue[field] = currentValue[field];
      }
    });

    setConfirmLoading(true);
    try {
      const isSuccess = await updateFilterSave({
        name: trimmedFilterName,
        data: JSON.stringify(filteredValue),
      });
      if (isSuccess && isMountedRef.current) {
        onSaved?.(trimmedFilterName, filteredValue as IFilterValues);
        handleClose();
      }
    } finally {
      if (isMountedRef.current) {
        setConfirmLoading(false);
      }
    }
  };

  const isSubmitDisabled =
    isEmpty(trimmedFilterName) ||
    trimmedFilterName.length > MAX_NAME_LENGTH ||
    isEmpty(checkValues) ||
    confirmLoading ||
    isCreateBlockedByLimit;

  return (
    <Modal
      className="dbi-save-model dbi-save-model-filter"
      size="l"
      caption={formatMessage({ id: 'dbi_filterSave_filter_setting' })}
      visible={visible}
      onClose={handleClose}
    >
      <Modal.Body>
        <div className="model-text">{formatMessage({ id: 'dbi_filterSave_setting_info' })}</div>
        <Form className="model-form">
          <Form.Item
            label={formatMessage({ id: 'dbi_filterSave_setting_name' })}
            showStatusIcon={false}
            message={validationMessage}
          >
            <Input
              className="model-input"
              placeholder={formatMessage({ id: 'dbi_filterSave_enter_a_name' })}
              value={filterName}
              onChange={handleNameChange}
            />
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbi_filterSave_save_item' })} showStatusIcon={false}>
            <CheckboxGroup
              className="model-form-group"
              value={checkValues}
              onChange={(vals) => {
                setCheckValues(vals);
              }}
            >
              {visibleItems.map((item) => {
                return (
                  <Checkbox key={item.field} className="model-form-checkbox" name={item.field}>
                    {renderItemPreview(item, currentValue[item.field], previewCtx)}
                  </Checkbox>
                );
              })}
            </CheckboxGroup>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          loading={confirmLoading}
          disabled={isSubmitDisabled}
          onClick={() => {
            handleConfirm().catch((err) => {
              console.debug('[Filter] SaveFilterPopup confirm error:', err);
            });
          }}
        >
          {formatMessage({ id: 'common_confirm' })}
        </Button>
        <Button type="weak" onClick={handleClose}>
          {formatMessage({ id: 'common_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveFilterPopup;
